import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { Button, Stack, Tooltip, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useMemo, useState, Suspense } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { SelectedPoiMenuButton } from './SelectedPoiMenuButton';
import { PoiSuspenseFallback } from './PoiSuspenseFallback';
import {
  PointOfInterest,
  useSelectedPoiUpdatedSubscription,
  usePointOfInterestQuery,
  PoiStatus,
} from '@/__generated__/graphql';
import { generatePoiDisplayName } from './utils';
import { abyssColors } from '@/theme/colors';
import { DetailsTab, GeneralTab } from './Tabs';
import { Comments } from '@/components/shared/Comments';
import { Logs } from './Logs/Logs';
import { useSetAtom } from 'jotai';
import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';
import { useIsolateCustomBlock } from '@/components/Analysis/modules/CustomBlocks/hooks';

interface TabPanelProps {
  children?: React.ReactNode;
  id: number;
  selectedTabId: number;
}

export function PoiCommentsAndLogsButtons({
  selectedTab,
  setSelectedTab,
}: {
  selectedTab: string;
  setSelectedTab: (tab: string) => void;
}) {
  return (
    <Stack direction="row" spacing={2} sx={{ background: abyssColors.primary[100], p: 1 }}>
      <Button
        variant="contained"
        sx={{
          color: selectedTab === 'comments' ? 'white' : 'inherit',
          backgroundColor: selectedTab === 'comments' ? abyssColors.primary[500] : 'inherit',
        }}
        onClick={() => setSelectedTab('comments')}
      >
        Comments
      </Button>
      <Button
        variant="contained"
        sx={{
          color: selectedTab === 'logs' ? 'white' : 'inherit',
          backgroundColor: selectedTab === 'logs' ? abyssColors.primary[500] : 'inherit',
        }}
        onClick={() => setSelectedTab('logs')}
      >
        Logs
      </Button>
    </Stack>
  );
}

export function TabContent(props: TabPanelProps) {
  const { children, selectedTabId, id, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={selectedTabId !== id}
      id={`simple-tabpanel-${id}`}
      style={{ flex: '1 1 auto' }}
      aria-labelledby={`simple-tab-${id}`}
      {...other}
    >
      {children}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

type Props = {
  selectedPoiId: string;
  closeHandler: () => void;
  disableEdit?: boolean;
  pointOfInterestDetails?: PointOfInterest;
  resetFocus?: boolean;
};

const headerStyles = {
  display: 'flex',
  alignItems: 'center',
  p: 1,
  py: 2,
  cursor: 'pointer',
  justifyContent: 'space-between',
};

export const SelectedPoiContent = ({
  selectedPoiId,
  closeHandler,
  disableEdit,
  resetFocus,
}: Props) => {
  const setSelectedPointOfInterestId = useSetAtom(poiState.selectedPointOfInterestId);
  const [selectedPoiInfo, setSelectedPoiInfo] = useState<PointOfInterest | undefined>(undefined);
  const [selectedTab, setSelectedTab] = useState<string>('comments');
  const { loading: selectedPoiInfoLoading, refetch } = usePointOfInterestQuery({
    variables: {
      pointOfInterestId: selectedPoiId,
    },

    skip: !selectedPoiId,
    notifyOnNetworkStatusChange: true, // onCompleted is not triggered after refetch if this is false
    onCompleted: (response) => {
      const data = response?.pointOfInterest;
      if (data) {
        setSelectedPoiInfo(data as PointOfInterest);
        isolateWorkScope(data as PointOfInterest);
      } else {
        setSelectedPoiInfo(undefined);
        // reset selectedPoiId when deleted / not found
        setSelectedPointOfInterestId(undefined);
      }
    },
  });

  useSelectedPoiUpdatedSubscription({
    variables: {
      poiID: selectedPoiId,
    },
    onData: () => {
      refetch();
    },
  });

  const { isolateWorkScope } = useIsolateCustomBlock({ resetFocus });

  const tabsData = useMemo(
    () => [
      {
        id: 0,
        label: 'Overview',
        component: selectedPoiInfo && (
          <Suspense fallback={<PoiSuspenseFallback />}>
            <GeneralTab
              selectedPoiInfo={selectedPoiInfo}
              selectedPoiInfoLoading={selectedPoiInfoLoading}
            />
          </Suspense>
        ),
      },
      {
        id: 1,
        label: 'Details',
        component: selectedPoiInfo && (
          <Suspense fallback={<PoiSuspenseFallback />}>
            <DetailsTab disableEdit={disableEdit} selectedPoiInfo={selectedPoiInfo} />
          </Suspense>
        ),
      },
      {
        id: 2,
        label: 'Comments/logs',
        component: (
          <>
            <Suspense fallback={<PoiSuspenseFallback />}>
              {selectedTab === 'comments' && (
                <>
                  <Box sx={{ height: '80vh', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ flexShrink: 0 }}>
                      <PoiCommentsAndLogsButtons
                        selectedTab={selectedTab}
                        setSelectedTab={setSelectedTab}
                      />
                    </Box>
                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                      <Comments referenceId={selectedPoiId} collectionName="pointOfInterests" />
                    </Box>
                  </Box>
                </>
              )}
              {selectedTab === 'logs' && (
                <Box sx={{ height: '80vh', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ flexShrink: 0 }}>
                    <PoiCommentsAndLogsButtons
                      selectedTab={selectedTab}
                      setSelectedTab={setSelectedTab}
                    />
                  </Box>
                  <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    <Logs
                      referenceId={selectedPoiId}
                      collectionName="PointOfInterest"
                      poiType={selectedPoiInfo?.type}
                      isLogsTab={selectedTab === 'logs'}
                    />
                  </Box>
                </Box>
              )}
            </Suspense>
          </>
        ),
      },
    ],
    [selectedPoiInfo, selectedPoiInfoLoading, disableEdit, selectedTab, selectedPoiId]
  );

  const [selectedTabId, setSelectedTabId] = useState(tabsData[0].id);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTabId(newValue);
  };

  if (!selectedPoiInfo) {
    return <PoiSuspenseFallback />;
  }

  const poiName = generatePoiDisplayName({
    name: selectedPoiInfo.name,
    poiId: selectedPoiInfo.poiId,
    poiType: selectedPoiInfo.type,
  });
  return (
    <Stack
      direction="column"
      sx={{ width: '100%', height: '100%', backgroundColor: abyssColors.primary[50] }}
    >
      <Box sx={headerStyles}>
        <Stack direction="row" alignItems="center" onClick={closeHandler}>
          <ArrowBack fontSize="medium" />
          <Tooltip title={poiName} placement="bottom">
            <Typography
              data-testid="poi-name"
              fontSize="1.8rem"
              sx={{
                ml: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '250px',
              }}
            >
              {poiName}
            </Typography>
          </Tooltip>
          {selectedPoiInfo.status === PoiStatus.Completed ||
          selectedPoiInfo.status === PoiStatus.Remediated ? (
            <Tooltip title="Completed/Remediated">
              <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
            </Tooltip>
          ) : selectedPoiInfo.isVerified ? (
            <Tooltip title="Verified">
              <CheckCircleIcon sx={{ color: '#0B9DEC', ml: 1 }} />
            </Tooltip>
          ) : (
            <></>
          )}
        </Stack>

        <SelectedPoiMenuButton onDelete={closeHandler} poiId={selectedPoiInfo.id} />
      </Box>

      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          '& button': { textTransform: 'none', fontWeight: 400 },
          '& .Mui-selected': { fontWeight: 500 },
        }}
      >
        <Tabs
          value={selectedTabId}
          onChange={handleChange}
          variant="fullWidth"
          sx={{
            '@media (max-width: 1728px)': {
              minHeight: '36px',
              '& button': {
                padding: 0,
                minHeight: '36px',
                minWidth: 0,
              },
            },
          }}
        >
          {tabsData.map((tab) => (
            <Tab label={tab.label} {...a11yProps(tab.id)} key={tab.id} />
          ))}
        </Tabs>
      </Box>
      {tabsData.map((tab) => (
        <TabContent key={tab.id} selectedTabId={selectedTabId} id={tab.id}>
          {tab.component}
        </TabContent>
      ))}
    </Stack>
  );
};
